<template>
  <div class="gap-1 rounded-lg bg-background-fg/3 p-4">
    <div class="mb-2 flex items-center justify-between">
      <button type="button" @click="decrementView">
        <IconChevronLeft class="h-full" />
      </button>
      <p class="font-semibold">
        {{
          props.type === "month"
            ? DateFns.format(currentMonthStart, "yyyy")
            : DateFns.format(currentMonthStart, "MMMM yyyy")
        }}
      </p>
      <button type="button" @click="incrementView">
        <IconChevronRight />
      </button>
    </div>
    <template v-if="props.type !== 'month'">
      <div class="mb-2 grid grid-rows-7 gap-1">
        <div class="grid grid-cols-7 gap-1">
          <template
            v-for="dayOfWeek in daysOfWeek"
            :key="dayOfWeek"
          >
            <div
              class="flex items-center justify-center text-sm text-background-fg/70"
            >
              {{ dayOfWeek }}
            </div>
          </template>
        </div>
        <template
          v-for="(dates, startOfWeek) in daysGroupedByWeeks"
          :key="startOfWeek"
        >
          <div class="group grid grid-cols-7 gap-1">
            <template
              v-for="date in dates"
              :key="date.toString()"
            >
              <div :class="$style.squareHack">
                <button
                  type="button"
                  :class="[
                    isDateActive(date).value
                      ? 'bg-primary text-primary-fg'
                      : isDateHovered(date)
                        ? 'bg-primary/10 hover:bg-primary/30'
                        : `bg-background text-background-fg ${
                            props.type === 'week'
                              ? 'group-hover:bg-primary/20'
                              : 'hover:bg-primary/20'
                          }`,

                    'absolute inset-0 w-full rounded font-semibold disabled:opacity-20',
                  ]"
                  :disabled="isDateDisabled(date)"
                  @click="handleClickDate(date)"
                  @mouseover="onHoverDate(date)"
                  @mouseout="onHoverDate(undefined)"
                >
                  {{ DateFns.getDate(date) }}
                </button>
              </div>
            </template>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="mb-2 grid grid-cols-3 grid-rows-4 gap-1">
        <template
          v-for="monthStartDate in monthsByStartDate"
          :key="monthStartDate.toString()"
        >
          <button
            type="button"
            :class="[
              isDateActive(monthStartDate).value
                ? 'bg-primary text-primary-fg'
                : 'bg-background text-background-fg hover:bg-primary/20',
              'w-full rounded py-6 font-semibold disabled:opacity-20',
            ]"
            :disabled="isDateDisabled(monthStartDate)"
            @click="handleClickDate(monthStartDate)"
          >
            {{ DateFns.format(monthStartDate, "MMM") }}
          </button>
        </template>
      </div>
    </template>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    TRangeType extends 'day' | 'week' | 'month' | 'custom'
  "
>
import * as DateFns from "date-fns"

export type BoundedRange = {
  start: string
  end: string
}

export type UnboundedRange = {
  start?: string
  end?: string
}
export type DateRangeSelectModelValue<
  T extends "day" | "week" | "month" | "custom",
> = T extends "custom" ? UnboundedRange : BoundedRange

const props = defineProps<{
  modelValue: DateRangeSelectModelValue<TRangeType>
  type: TRangeType
}>()

const emit = defineEmits<{
  (
    eventName: "update:modelValue",
    value: DateRangeSelectModelValue<TRangeType>
  ): void
}>()

const modelValueAsDate = computed(() => ({
  start: props.modelValue.start
    ? DateFns.parse(
        props.modelValue.start,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined,
  end: props.modelValue.end
    ? DateFns.parse(
        props.modelValue.end,
        "yyyy-MM-dd",
        new Date()
      )
    : undefined,
}))

const getCurrentMonthStart = (date: Date | undefined) => {
  return date
    ? DateFns.startOfMonth(date)
    : DateFns.startOfMonth(new Date())
}

const dateRangeStart = ref<Date>()

const hoveredDate = ref<Date | undefined>()
const onHoverDate = (date: Date | undefined) => {
  hoveredDate.value = date
}
const isDateHovered = (date: Date) => {
  if (props.type !== "custom") return false
  if (!dateRangeStart.value || !hoveredDate.value) {
    return false
  } else if (
    hoveredDate.value &&
    DateFns.isWithinInterval(date, {
      start: DateFns.min([
        dateRangeStart.value,
        hoveredDate.value,
      ]),
      end: DateFns.max([
        dateRangeStart.value,
        hoveredDate.value,
      ]),
    })
  ) {
    return true
  } else {
    return false
  }
}
const currentMonthStart = ref(
  getCurrentMonthStart(
    modelValueAsDate.value?.start || new Date()
  )
)
const decrementView = () => {
  currentMonthStart.value =
    props.type === "month"
      ? DateFns.addYears(currentMonthStart.value, -1)
      : DateFns.addMonths(currentMonthStart.value, -1)
}

const incrementView = () => {
  currentMonthStart.value =
    props.type === "month"
      ? DateFns.addYears(currentMonthStart.value, 1)
      : DateFns.addMonths(currentMonthStart.value, 1)
}

const visibleDates = computed(() =>
  DateFns.eachDayOfInterval({
    start: DateFns.startOfISOWeek(currentMonthStart.value),
    end: DateFns.endOfISOWeek(
      DateFns.endOfMonth(currentMonthStart.value)
    ),
  })
)

const handleClickDate = (date: Date) => {
  switch (props.type) {
    case "day": {
      emit("update:modelValue", {
        start: DateFns.format(date, "yyyy-MM-dd"),
        end: DateFns.format(date, "yyyy-MM-dd"),
      })

      break
    }
    case "week": {
      emit("update:modelValue", {
        start: DateFns.format(
          DateFns.startOfISOWeek(date),
          "yyyy-MM-dd"
        ),
        end: DateFns.format(
          DateFns.endOfISOWeek(date),
          "yyyy-MM-dd"
        ),
      })

      break
    }
    case "month": {
      emit("update:modelValue", {
        start: DateFns.format(
          DateFns.startOfMonth(date),
          "yyyy-MM-dd"
        ),
        end: DateFns.format(
          DateFns.endOfMonth(date),
          "yyyy-MM-dd"
        ),
      })

      break
    }
    default: {
      if (!dateRangeStart.value) {
        dateRangeStart.value = date
      } else if (dateRangeStart.value) {
        emit("update:modelValue", {
          start: DateFns.format(
            DateFns.min([dateRangeStart.value, date]),
            "yyyy-MM-dd"
          ),
          end: DateFns.format(
            DateFns.max([dateRangeStart.value, date]),
            "yyyy-MM-dd"
          ),
        })
        dateRangeStart.value = undefined
      } else {
        dateRangeStart.value = date
      }
    }
  }
}

const isDateActive = (date: Date) =>
  computed(() => {
    if (dateRangeStart.value) {
      return DateFns.isSameDay(dateRangeStart.value, date)
    } else if (
      !modelValueAsDate.value.start &&
      !modelValueAsDate.value.end
    ) {
      return false
    } else if (
      !modelValueAsDate.value.start &&
      modelValueAsDate.value.end
    ) {
      return DateFns.isBefore(
        date,
        modelValueAsDate.value.end
      )
    } else if (
      modelValueAsDate.value.start &&
      !modelValueAsDate.value.end
    ) {
      return DateFns.isAfter(
        date,
        modelValueAsDate.value.start
      )
    } else if (
      modelValueAsDate.value.start &&
      modelValueAsDate.value.end
    ) {
      return DateFns.isWithinInterval(date, {
        start: modelValueAsDate.value.start,
        end: modelValueAsDate.value.end,
      })
    }
    return ""
  })

const isDateDisabled = (date: Date) =>
  DateFns.isFuture(date) ||
  (props.type !== "month" &&
    !DateFns.isSameMonth(date, currentMonthStart.value))

const daysOfWeek = [
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
  "Su",
]

const daysGroupedByWeeks = computed(() =>
  groupBy(visibleDates.value, (date) =>
    DateFns.format(
      DateFns.startOfISOWeek(date),
      "yyyy-MM-dd"
    )
  )
)

const monthsByStartDate = computed(() =>
  DateFns.eachMonthOfInterval({
    start: DateFns.startOfYear(currentMonthStart.value),
    end: DateFns.endOfYear(
      DateFns.endOfMonth(currentMonthStart.value)
    ),
  })
)
</script>

<style lang="scss" module>
.squareHack {
  position: relative;
  padding-top: 100%;
}
</style>
